import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/core/auth/AuthProvider";
import { Shield, Building2, Briefcase, ChevronRight, Eye } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { cn } from "@/shared/utils/cn";

interface ContextStatusBarProps {
  onOpenContextPicker?: () => void;
  className?: string;
}

export function ContextStatusBar({ onOpenContextPicker, className }: ContextStatusBarProps) {
  const { hasRole } = useAuth();
  const {
    currentOrg,
    currentClient,
    isAdminMode,
    setAdminMode,
    impersonatedRole,
    effectiveRole,
    setImpersonatedRole,
  } = useTenant();

  const isAdmin = hasRole("admin");

  const getViewModeInfo = () => {
    if (isAdminMode || effectiveRole === 'admin') {
      return {
        icon: <Shield className="h-3.5 w-3.5" />,
        label: "Admin Mode",
        sublabel: "Platform-wide access",
        color: "text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/50 dark:border-amber-800",
      };
    }
    if (effectiveRole === 'agency_owner' && currentOrg) {
      return {
        icon: <Building2 className="h-3.5 w-3.5" />,
        label: currentOrg.name,
        sublabel: currentClient ? `→ ${currentClient.name}` : "No client selected",
        color: "text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/50 dark:border-blue-800",
      };
    }
    if (currentClient) {
      return {
        icon: <Briefcase className="h-3.5 w-3.5" />,
        label: currentClient.name,
        sublabel: currentOrg?.name || "Client",
        color: "text-green-600 bg-green-50 border-green-200 dark:bg-green-950/50 dark:border-green-800",
      };
    }
    return {
      icon: <Eye className="h-3.5 w-3.5" />,
      label: "No context",
      sublabel: "Select a client",
      color: "text-muted-foreground bg-muted border-border",
    };
  };

  const viewMode = getViewModeInfo();

  const handleQuickSwitch = (mode: 'admin' | 'agency' | 'client') => {
    if (!isAdmin) return;

    if (mode === 'admin') {
      setImpersonatedRole('admin');
      setAdminMode(true);
    } else if (mode === 'agency') {
      setImpersonatedRole('agency_owner');
    } else {
      setImpersonatedRole('company_owner');
    }
  };

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 border-b bg-background/50",
        className
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2 px-2 py-1 rounded-md border text-sm",
          viewMode.color
        )}
      >
        {viewMode.icon}
        <span className="font-medium">{viewMode.label}</span>
        {viewMode.sublabel && (
          <>
            <ChevronRight className="h-3 w-3 opacity-50" />
            <span className="text-xs opacity-75">{viewMode.sublabel}</span>
          </>
        )}
      </div>

      {impersonatedRole && (
        <Badge variant="outline" className="text-xs gap-1">
          <Eye className="h-3 w-3" />
          Viewing as {impersonatedRole === 'admin' ? 'Admin' : 
                      impersonatedRole === 'agency_owner' ? 'Agency' : 'Client'}
        </Badge>
      )}

      <div className="flex-1" />

      {isAdmin && (
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground mr-1">Quick switch:</span>
          <Button
            variant={effectiveRole === 'admin' ? "default" : "ghost"}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => handleQuickSwitch('admin')}
          >
            <Shield className="h-3 w-3 mr-1" />
            Admin
          </Button>
          <Button
            variant={effectiveRole === 'agency_owner' ? "default" : "ghost"}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => handleQuickSwitch('agency')}
          >
            <Building2 className="h-3 w-3 mr-1" />
            Agency
          </Button>
          <Button
            variant={effectiveRole === 'company_owner' ? "default" : "ghost"}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => handleQuickSwitch('client')}
          >
            <Briefcase className="h-3 w-3 mr-1" />
            Client
          </Button>
        </div>
      )}

      {onOpenContextPicker && !isAdminMode && (
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={onOpenContextPicker}
        >
          Change
        </Button>
      )}
    </div>
  );
}
